import { TableConfig } from '@/components/table/types';
import { Icon } from '@iconify/react';
import { Button } from '@nextui-org/react';
import DataTable from 'Components/table/data-table';
import { useEffect, useState } from 'react';
import DatasetInfoPanel from '../components/dataset-info-panel';
import TestCaseSidePanel from '../components/test-case-side-panel';

const generateId = () => `tc_${Math.random().toString(36).substr(3, 9)}`;

interface TestCase {
  id: string;
  label: string;
  chat_history: string;
  target: string;
}

const DEFAULT_TEST_CASES: TestCase[] = [
  {
    id: 'tc_1',
    label: 'Basic Greeting',
    chat_history: 'User: Hello\nAssistant: Hi, how can I help you today?',
    target: 'Friendly and professional greeting',
  },
  {
    id: 'tc_2',
    label: 'Technical Support',
    chat_history:
      "User: My computer won't start\nAssistant: Let's troubleshoot this together. First, is the power cable properly connected?",
    target: 'Clear technical assistance steps',
  },
  {
    id: 'tc_3',
    label: 'Product Inquiry',
    chat_history:
      "User: What's the price of your basic plan?\nAssistant: Our basic plan starts at $9.99 per month and includes all essential features.",
    target: 'Accurate pricing information',
  },
];

interface Dataset {
  id: string;
  name: string;
  description?: string;
  testCases: TestCase[];
}

const DEFAULT_DATASETS: Dataset[] = [
  {
    id: 'ds_1',
    name: 'General Conversation Tests',
    description: 'Basic conversation scenarios',
    testCases: DEFAULT_TEST_CASES,
  },
  {
    id: 'ds_2',
    name: 'Technical Support Tests',
    description: 'Support-related scenarios',
    testCases: [],
  },
];

const datasetsTableConfig: TableConfig = {
  className: 'w-full',
  columns: [
    {
      uid: 'name',
      name: 'Name',
      sortable: true,
    },
    {
      uid: 'description',
      name: 'Description',
      sortable: true,
    },
    {
      uid: 'testCases',
      name: 'Test Cases',
      renderCell: (row: Dataset) => row.testCases.length,
    },
  ],
  initialVisibleColumns: ['name', 'description', 'testCases'],
  actions: {
    enableRowSelection: false,
    defaultRowSelection: 'single',
  },
  searchKey: 'name',
  searchable: true,
  searchPlaceholder: 'Search datasets...',
  pagination: {
    initialRowsPerPage: 50,
  },
  bottomContentPlacement: 'outside',
  tdClassName: 'py-2.5',
};

export default function DataSetsTab({
  onShowAddButtonChange,
}: {
  onShowAddButtonChange?: (show: boolean) => void;
}) {
  const datasets = DEFAULT_DATASETS;
  const [selectedDataset, setSelectedDataset] = useState<Dataset | undefined>();
  const [data, setData] = useState<TestCase[]>(DEFAULT_TEST_CASES);
  const [selectedTestCase, setSelectedTestCase] = useState<
    TestCase | undefined
  >();
  const [isEditing, setIsEditing] = useState(false);
  const [tempId, setTempId] = useState<string | null>(null);

  console.log(data);

  useEffect(() => {
    onShowAddButtonChange?.(!selectedDataset);
  }, [selectedDataset, onShowAddButtonChange]);

  const handleSave = (formData: Omit<TestCase, 'id'>) => {
    if (!tempId) {
      // Update existing test case
      setData((prevData) =>
        prevData.map((item) =>
          item.id === selectedTestCase?.id ? { ...item, ...formData } : item
        )
      );
    } else {
      // Add new test case with the temp id
      const newTestCase: TestCase = {
        id: tempId!,
        ...formData,
      };
      setData((prevData) => [...prevData, newTestCase]);
    }
    setIsEditing(false);
    setSelectedTestCase(undefined);
    setTempId(null);
  };

  const handleAddTestCase = () => {
    const newTempId = generateId();
    // Add temporary data
    setSelectedTestCase(undefined);
    const tempTestCase: TestCase = {
      id: newTempId,
      label: 'New Test Case',
      chat_history: '',
      target: '',
    };
    // setData((prevData) => [...prevData, tempTestCase]);
    setTempId(newTempId);
    setSelectedTestCase(tempTestCase);
    setIsEditing(true);
  };

  const handleClose = () => {
    // Remove temporary data if not saved
    if (tempId) {
      setData((prevData) => prevData.filter((item) => item.id !== tempId));
    }
    setIsEditing(false);
    setSelectedTestCase(undefined);
    setTempId(null);
  };

  // Handle row selection
  const handleSelectionChange = (selection: any) => {
    // Assuming selection contains the selected row id
    const selectedId = selection.currentKey;
    if (selectedId) {
      const selected = data.find((item) => item.id === selectedId);
      if (selected) {
        setSelectedTestCase(selected);
        setIsEditing(true);
      }
    }
  };

  const handleDelete = (id: string) => {
    setData((prevData) => prevData.filter((item) => item.id !== id));
    // If the deleted item was selected, clear the selection
    if (selectedTestCase?.id === id) {
      setSelectedTestCase(undefined);
      setIsEditing(false);
    }
  };

  const tableConfig: TableConfig = {
    className: 'w-full',
    columns: [
      {
        uid: 'label',
        name: 'Label',
        sortable: true,
      },
      {
        uid: 'chat_history',
        name: 'Chat History',
        sortable: true,
      },
      {
        uid: 'target',
        name: 'Target',
        sortable: true,
      },
    ],
    initialVisibleColumns: ['label', 'chat_history', 'target'],
    actions: {
      enableRowSelection: false,
      defaultRowSelection: 'single',
    },
    bottomContentPlacement: 'outside',
    tdClassName: 'py-2.5',
  };

  // Fix the dataset selection handler
  const handleDatasetSelection = (selection: any) => {
    const selectedId = selection.currentKey;
    const selected = datasets.find((dataset) => dataset.id === selectedId);
    setSelectedDataset(selected);
    setData(selected?.testCases || []);
  };

  return (
    <div
      className={
        selectedDataset ? 'flex min-h-[60vh] items-stretch gap-0' : undefined
      }
    >
      <div
        className={
          selectedDataset ? 'min-w-0 flex-1 space-y-4 pr-6' : 'space-y-4'
        }
      >
        {!selectedDataset && (
          <>
            <div className="hidden sm:flex justify-end">
              <Button
                startContent={<Icon icon="mdi:plus" />}
                color="primary"
                // onPress={handleAddDataset}
                size="sm"
              >
                Add Dataset
              </Button>
            </div>
            <DataTable
              config={datasetsTableConfig}
              data={datasets}
              onSelectionChange={handleDatasetSelection}
            />
          </>
        )}
        {selectedDataset && (
          <>
            <div className="flex justify-between items-center">
              <Button
                startContent={<Icon icon="mdi:arrow-left" />}
                variant="light"
                onPress={() => setSelectedDataset(undefined)}
                size="sm"
              >
                Back to Datasets
              </Button>
              <Button
                startContent={<Icon icon="mdi:plus" />}
                color="primary"
                onPress={handleAddTestCase}
                size="sm"
              >
                Add Test Case
              </Button>
            </div>
            <DataTable
              config={tableConfig}
              data={data}
              onSelectionChange={handleSelectionChange}
            />
          </>
        )}
      </div>

      {selectedDataset && (
        <div className="flex w-[500px] shrink-0 flex-col border-l pl-6">
          {isEditing ? (
            <TestCaseSidePanel
              isOpen={isEditing}
              onClose={handleClose}
              testCase={selectedTestCase}
              onSubmit={handleSave}
              isNewCase={Boolean(tempId)}
              handleDelete={handleDelete}
            />
          ) : (
            <DatasetInfoPanel />
          )}
        </div>
      )}
    </div>
  );
}
